import { flattenNoteText } from "./noteMentions";

/**
 * Draft storage and the idempotency-key rules of the note composer (D-04, SC4).
 *
 * A draft is the text of a composer that was not saved yet, plus the key that the save
 * of that text travels under. The key is minted when the draft is created (a composer
 * opens, or after a successful save) and reused for every retry of an unchanged body,
 * so a retried or double-submitted save is one note on the server. Only when the body
 * was changed after a FAILED save a new key is minted: the trade-off is a possible
 * visible duplicate (the author can delete it) instead of silently dropping the edit.
 *
 * Pure: storage and id minting are injected. No React, no DOM.
 */

/** `edit` changes a stored note or reply in place; its drafts are keyed by the edited message and it mints no key. */
export type NoteComposerVariant = "note" | "reply" | "edit";

export interface NoteDraft {
  text: string;
  /** The key the next save of this draft is sent under. */
  clientRequestId: string;
  /** The body of the last save that failed; `null` while no save failed since the key was minted. */
  failedText: string | null;
}

/** The slice of `Storage` the drafts use, so tests pass an in-memory object. */
export type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Server limit, counted in Unicode code points of the flattened text (jupiter/services/notes.py). */
export const MAX_NOTE_CHARS = 10000;

/** Web Crypto, the only source of keys. A function reference, so no call site can mint one by accident. */
export const mintClientRequestId = (): string => crypto.randomUUID();

/** One slot per conversation, composer variant and answered message (`root` for a new note). */
export function draftStorageKey(
  conversationId: string,
  variant: NoteComposerVariant,
  targetMessageId?: string | null,
): string {
  return `lytra.noteDraft.${conversationId}.${variant}.${targetMessageId ?? "root"}`;
}

const freshDraft = (mint: () => string): NoteDraft => ({
  text: "",
  clientRequestId: mint(),
  failedText: null,
});

const isDraft = (value: unknown): value is NoteDraft =>
  typeof value === "object" &&
  value !== null &&
  "text" in value &&
  typeof value.text === "string" &&
  "clientRequestId" in value &&
  typeof value.clientRequestId === "string" &&
  "failedText" in value &&
  (value.failedText === null || typeof value.failedText === "string");

/** The stored draft for `key`, or a fresh one (empty text, new key). A corrupt entry counts as none. */
export function loadDraft(
  storage: DraftStorage,
  key: string,
  mint: () => string = mintClientRequestId,
): NoteDraft {
  try {
    const raw = storage.getItem(key);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (isDraft(parsed)) return parsed;
    }
  } catch {
    // Unreadable storage or JSON: start fresh, the draft is a convenience.
  }
  return freshDraft(mint);
}

/** Storage may be full or blocked (private mode); a lost draft must never break the composer. */
export function saveDraft(
  storage: DraftStorage,
  key: string,
  draft: NoteDraft,
): void {
  try {
    storage.setItem(key, JSON.stringify(draft));
  } catch {
    // Ignored on purpose, see above.
  }
}

export function clearDraft(storage: DraftStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Ignored on purpose, see saveDraft.
  }
}

/**
 * The key to send `text` under. An unchanged body after a failure, and a first submit,
 * reuse the draft's key; a body changed after a failed save gets a new key and a clean
 * failure record.
 */
export function keyForSubmit(
  draft: NoteDraft,
  text: string,
  mint: () => string = mintClientRequestId,
): { clientRequestId: string; draft: NoteDraft } {
  if (draft.failedText !== null && text !== draft.failedText) {
    const next: NoteDraft = {
      text,
      clientRequestId: mint(),
      failedText: null,
    };
    return { clientRequestId: next.clientRequestId, draft: next };
  }
  return {
    clientRequestId: draft.clientRequestId,
    draft: { ...draft, text },
  };
}

/** The draft after a save of `text` failed: the text stays, the key stays, the body is remembered. */
export function afterFailure(draft: NoteDraft, text: string): NoteDraft {
  return { ...draft, text, failedText: text };
}

/** The length the server enforces: code points of the flattened text (a mention token counts as `@Label`). */
export function noteLength(text: string): number {
  return Array.from(flattenNoteText(text)).length;
}

/** The collapsed "Notiz hinzufügen" button of a ticket; focus returns here after a delete. */
export const noteComposerTriggerId = (conversationId: string): string =>
  `note-composer-trigger-${conversationId}`;
